import { useLocation, useNavigate } from "react-router";
import { Sparkles } from "lucide-react";
import { useAllowedPages } from "@/hooks/use-rbac";
import { isPathAllowed } from "@shared/rbac";
import { FLOATING_PILL_CLASS } from "@/lib/floating-pill";
import { FloatingPillSlot } from "@/components/FloatingPillRail";
import { useAnalystPanel } from "@/components/analyst/AnalystProvider";

/**
 * Floating "Ask Crosbot" launcher (GH #596). Opens the Analyst panel (#2088)
 * rather than navigating away, so the question and the page stay together.
 *
 * The single opener at every width (client ask on #2301): it used to hide on
 * lg+ in favour of the panel's own edge rail, but that rail is gone now, so
 * this pill is the only way in on every screen size. In presentation mode
 * (#1951) there is no panel, so the pill keeps its old job of jumping to the
 * full page with the flag intact.
 *
 * Visibility is RBAC-gated client-side as a courtesy (hidden when the role can't
 * reach /analyst) — but /analyst is enforced server-side regardless, so this is
 * purely a discoverability affordance, not a security control. Hidden on the
 * analyst page itself, while the panel is open, and while the role is still
 * loading (avoids a cold flash).
 *
 * #2603: renders through FloatingPillRail rather than owning a right-*
 * offset. `order-first` keeps it pinned to the rail's right edge (the
 * flex-row-reverse main-start) regardless of which pill mounts first; the
 * presentation-mode offset now lives on the rail itself.
 */
export function AskCrosbotButton() {
  const { openPanel, open } = useAnalystPanel();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const pages = useAllowedPages();
  const presentationMode = new URLSearchParams(search).get("present") === "1";

  if (pages === null) return null; // role not loaded yet
  if (!isPathAllowed("/analyst", pages)) return null;
  if (pathname === "/analyst") return null;
  if (open) return null;

  return (
    <FloatingPillSlot>
      <button
        type="button"
        // #1951: a capture session has no panel (the shell hides its chrome), so
        // there the pill still carries presentation mode to the full page.
        onClick={() => (presentationMode ? navigate("/analyst?present=1") : openPanel())}
        title="Ask Crosbot"
        aria-label="Ask Crosbot"
        className={`${FLOATING_PILL_CLASS} order-first`}
      >
        <Sparkles className="h-4 w-4 shrink-0" />
        <span className="hidden sm:inline">Ask Crosbot</span>
      </button>
    </FloatingPillSlot>
  );
}
